import posthog from "posthog-js"

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY
let globalErrorHandlersRegistered = false

function truncate(value: string, maxLength = 300) {
  return value.length > maxLength ? `${value.slice(0, maxLength)}...` : value
}

function getErrorDetails(error: unknown) {
  if (error instanceof Error) {
    return {
      name: error.name,
      message: truncate(error.message || "Unknown error"),
      stack: error.stack ? truncate(error.stack, 1200) : undefined,
    }
  }

  if (typeof error === "string") {
    return {
      name: "Error",
      message: truncate(error),
    }
  }

  if (error && typeof error === "object") {
    const maybeMessage =
      "message" in error && typeof error.message === "string" ? error.message : null
    const maybeName = "name" in error && typeof error.name === "string" ? error.name : "Error"

    return {
      name: maybeName,
      message: truncate(maybeMessage || JSON.stringify(error)),
    }
  }

  return {
    name: "Error",
    message: "Unknown error",
  }
}

export function initPostHog() {
  if (!POSTHOG_KEY) {
    console.warn("[PostHog] No API key provided, analytics disabled")
    return
  }

  posthog.init(POSTHOG_KEY, {
    api_host: "https://app.posthog.com",
    capture_pageview: true,
    capture_pageleave: true,
    autocapture: false,
  })
}

export function identifyUser(userId: string, traits?: Record<string, unknown>) {
  if (!POSTHOG_KEY) return
  posthog.identify(userId, traits)
}

export function resetUser() {
  if (!POSTHOG_KEY) return
  posthog.reset()
}

export function track(event: string, properties?: Record<string, unknown>) {
  if (!POSTHOG_KEY) return
  posthog.capture(event, properties)
}

export function captureBrowserException(error: unknown, context?: Record<string, unknown>) {
  if (!POSTHOG_KEY) return
  posthog.capture("browser_exception", {
    ...getErrorDetails(error),
    ...context,
    pathname: window.location.pathname,
    search: window.location.search || undefined,
  })
}

export function captureUnhandledRejection(reason: unknown, context?: Record<string, unknown>) {
  if (!POSTHOG_KEY) return
  posthog.capture("browser_unhandled_rejection", {
    ...getErrorDetails(reason),
    ...context,
    pathname: window.location.pathname,
    search: window.location.search || undefined,
  })
}

export function captureApiResponseError(properties: {
  path: string
  method: string
  status: number
  code?: string
  message: string
  isRetry: boolean
  skipAuth?: boolean
}) {
  if (!POSTHOG_KEY) return
  posthog.capture("api_response_error", {
    ...properties,
    pathname: window.location.pathname,
  })
}

export function captureApiRequestFailed(properties: {
  path: string
  method: string
  failure_kind: "network" | "abort"
  message: string
  isRetry: boolean
  skipAuth?: boolean
}) {
  if (!POSTHOG_KEY) return
  posthog.capture("api_request_failed", {
    ...properties,
    pathname: window.location.pathname,
  })
}

export function registerGlobalErrorHandlers() {
  if (!POSTHOG_KEY || globalErrorHandlersRegistered) {
    return
  }

  globalErrorHandlersRegistered = true

  window.addEventListener("error", (event) => {
    captureBrowserException(event.error || event.message, {
      source: "window.error",
      filename: event.filename || undefined,
      lineno: event.lineno || undefined,
      colno: event.colno || undefined,
    })
  })

  window.addEventListener("unhandledrejection", (event) => {
    captureUnhandledRejection(event.reason, {
      source: "window.unhandledrejection",
    })
  })
}

export { posthog }
